import React from 'react';
import { Tag, Typography } from '@guildeducationinc/recess';
import { calculateDaysUntilAppDue } from '@guildeducationinc/catalog-tools';
import {
	AcademicPartnerMerchandisingTier,
	GetMemberProgramQuery,
} from 'src/graphqlTypes/catalogServiceTypes';
import { formatDate } from 'src/utilities/dateUtility';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { formatStartDate } from '@src/utilities/programAttributeUtility';
import { PlanProgramStatus } from '@src/constants/planProgramStatus';
import { StartApplicationButton } from '@src/components/ProgramDetails/StartApplicationButton';
import styles from './StandardDateCta.module.scss';

interface StandardDateCtaProps {
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
}

export const StandardDateCta: React.FC<StandardDateCtaProps> = ({ program, loading }) => {
	const { memberPlanData } = useSearchContext();
	const eligible = memberPlanData.eligibility.eligible;

	const { merchandisingTier } = program?.studentFacingAcademicPartner || {};
	const { appDueDate, startDate } = program?.nextDate || {};
	const isCapped = program.planProgramStatus === PlanProgramStatus.CAPPED;

	// Since transitioning to UTC, we've been displaying the application deadline date that we get from catalog service
	// as the day BEFORE the actual deadline. In order for our date presentation to match the date logic in airtable/
	// catalog service, we need to add 1 day in inclusiveAppDueDate before formatting.
	const formattedApplicationDeadline = () => {
		if (appDueDate) {
			const exclusiveAppDueDate = new Date(appDueDate);
			const inclusiveAppDueDate = exclusiveAppDueDate.setDate(exclusiveAppDueDate.getDate() + 1);

			const date = formatDate(new Date(inclusiveAppDueDate), {
				month: '2-digit',
				day: '2-digit',
				year: 'numeric',
			});

			const time = formatDate(new Date(inclusiveAppDueDate), {
				hour: 'numeric',
				timeZoneName: 'short',
			});

			return `${date} at ${time}`;
		}

		if (merchandisingTier === AcademicPartnerMerchandisingTier.AFFILIATE) {
			return 'Rolling - Apply any time';
		}
		return 'Coming Soon';
	};

	const applicationDeadline = formattedApplicationDeadline().split(',');
	const applicationDeadlineDate =
		applicationDeadline.length > 1
			? `${applicationDeadline[0]}, ${applicationDeadline[1]}`
			: applicationDeadline[0];

	const displayAppDue = () => {
		if (!startDate || isCapped) {
			return;
		}

		if (calculateDaysUntilAppDue(appDueDate) === 0) {
			return (
				<Typography className={styles.appDue} variant='body1'>
					Deadline to apply
					<Tag appearance='quiet' className={styles.tag} color='yellow'>
						today
					</Tag>
				</Typography>
			);
		}

		if (calculateDaysUntilAppDue(appDueDate) === 1) {
			return (
				<Typography className={styles.appDue} variant='body1'>
					Deadline to apply in
					<Tag appearance='quiet' className={styles.tag} color='yellow'>
						1 day
					</Tag>
				</Typography>
			);
		}

		return (
			<Typography className={styles.appDue} variant='body1'>
				Deadline to apply in
				{calculateDaysUntilAppDue(appDueDate) <= 30 ? (
					<Tag appearance='quiet' className={styles.tag} color='yellow'>
						{calculateDaysUntilAppDue(appDueDate)} days
					</Tag>
				) : (
					<Typography className={styles.appDue30PlusDays} variant='body1' weight='bold'>
						{calculateDaysUntilAppDue(appDueDate)} days
					</Typography>
				)}
			</Typography>
		);
	};

	return (
		<div data-testid='date-cta'>
			<Skeleton className={styles.skeleton} dataLoading={loading}>
				<Typography className={styles.appDeadline} testid='deadline-date' variant='label1'>
					Deadline to apply: <Typography weight='normal'>{applicationDeadlineDate}</Typography>
				</Typography>
			</Skeleton>
			<Skeleton className={styles.skeleton} dataLoading={loading}>
				<Typography className={styles.programStart} testid='start-date' variant='label1'>
					Next start date: <Typography weight='normal'>{formatStartDate(program)}</Typography>
				</Typography>
			</Skeleton>

			{eligible && !isCapped ? (
				<>
					<Skeleton className={styles.skeleton} dataLoading={loading}>
						<StartApplicationButton program={program} />
					</Skeleton>

					<Skeleton className={styles.skeletonAppdue} dataLoading={loading}>
						{displayAppDue()}
					</Skeleton>
				</>
			) : null}
		</div>
	);
};
